import { useQueryClient } from "@tanstack/react-query";

import { ReviewCard } from "~/components/ReviewCard";
import { reportActionError } from "~/lib/actionErrors";
import { caseTasksQueryKey, type CaseTasksResponse } from "~/lib/cases";
import {
  useChangeTaskState,
  type EventState,
  type TaskCondition,
} from "~/lib/events";
import { asTaskState, gateVerdict, type Task } from "~/lib/tasks";

// Approving the identity review IS the human confirming the client's name
// matches the ID, so it also completes the manual "Verify client name" NCD
// checklist task — a workflow rule the SPA owns (TMF-208). The two changes go
// out as one event via the companion conditions below.
const IDENTITY_REVIEW_TASK_TYPE = "review identity verdict";
const VERIFY_CLIENT_NAME_TASK_TYPE = "Verify client name matches ID cards";

/** The task-state action a case's gates are driven by, plus the in-flight
 *  labels its controls read. Shaped exactly like the props TaskTable already
 *  takes, so the case page passes them straight through. */
export interface CaseTaskIssue {
  issue: (task: Task, targetState: EventState) => void;
  pendingTaskName: string | undefined;
  pendingState: EventState | undefined;
}

/**
 * useCaseTaskIssue owns "the human acted on a task of this case" — the event
 * POST plus the optimistic cache write the case detail page has always done
 * (TMF-208). Extracted out of the case page (TMF-357) so the upload review
 * queue can complete a case's gates through the exact same mutation and cache
 * mechanics rather than a parallel implementation.
 *
 * `onIssued` fires once the event POST succeeds; the case page passes nothing
 * (its own invalidation is handled inside useChangeTaskState), the review queue
 * passes its freshness hook.
 *
 * Call this from a component that OUTLIVES the optimistic write below: the write
 * can remove the control that called `issue` from the tree, and React Query only
 * runs mutate()-scoped callbacks — `onIssued` among them — while the calling
 * component is still mounted (TMF-363).
 */
export function useCaseTaskIssue(
  caseId: string,
  projectId: string,
  onIssued?: () => void,
): CaseTaskIssue {
  const queryClient = useQueryClient();
  const mutation = useChangeTaskState();

  // Issue a task-state change. Approving the identity review also completes the
  // "Verify client name" checklist task, posted as a companion in the same
  // event (TMF-208). Optimistic update flips the cached tasks so the row
  // updates instantly; rollback on error, the success path refetches.
  const issue = (task: Task, targetState: EventState) => {
    const key = caseTasksQueryKey(caseId);
    const prev = queryClient.getQueryData<CaseTasksResponse>(key);

    const companions: TaskCondition[] =
      targetState === "complete" && task.type === IDENTITY_REVIEW_TASK_TYPE
        ? (prev?.tasks ?? [])
            .filter((t) => t.type === VERIFY_CLIENT_NAME_TASK_TYPE && t.state !== "complete")
            .map((t) => ({ taskName: t.name, state: "complete" as EventState }))
        : [];

    const companionNames = new Set(companions.map((c) => c.taskName));
    // The engine's event vocabulary is wider than a task's ("un-cancel",
    // "error", ""), and only the overlap can be written into a cached row.
    // The UI issues nothing outside it today; anything that ever does leaves
    // the row alone and lets the refetch say what happened.
    const optimistic = asTaskState(targetState);
    queryClient.setQueryData<CaseTasksResponse>(key, (old) => {
      if (old === undefined) return old;
      return {
        ...old,
        tasks: old.tasks.map((t) => {
          if (t.id === task.id) return optimistic === null ? t : { ...t, state: optimistic };
          if (companionNames.has(t.name)) return { ...t, state: "complete" };
          return t;
        }),
      };
    });
    mutation.mutate(
      { projectId, caseId, taskName: task.name, state: targetState, companions },
      {
        onError: (error) => {
          if (prev !== undefined) queryClient.setQueryData(key, prev);
          // Rolling the row back silently made a server rejection
          // indistinguishable from a dead button — say what the server said
          // (TMF-362).
          reportActionError(error);
        },
        onSuccess: () => {
          onIssued?.();
        },
      },
    );
  };

  return {
    issue,
    pendingTaskName: mutation.isPending ? mutation.variables?.taskName : undefined,
    pendingState: mutation.isPending ? mutation.variables?.state : undefined,
  };
}

export interface CaseGateCardProps {
  /** An OPEN review gate on the case's project (isOpenReviewGate). */
  task: Task;
  /** The case-task action driving this gate, owned by whoever mounts the card.
   *  Deliberately NOT created here: `issue`'s optimistic write flips the gate to
   *  `complete`, which in the upload review queue takes this very card out of
   *  the tree, and React Query drops mutate()-scoped callbacks when the
   *  component that called mutate() unmounts first (TMF-363). The owner outlives
   *  the flip, so its callbacks survive it. Passed down exactly like the
   *  exception arm's `resolve` mutation. */
  caseTask: CaseTaskIssue;
}

/**
 * CaseGateCard is one of a CASE's review gates rendered standalone — the same
 * ReviewCard the case page's TaskTable mounts in its expansion row, driven by
 * the same useCaseTaskIssue mutation. It exists so the document-upload review
 * queue can present a case-attached document's gate in place (TMF-357) instead
 * of dead-ending the reviewer with a link to the case page.
 */
export function CaseGateCard({ task, caseTask }: CaseGateCardProps) {
  const { issue, pendingTaskName, pendingState } = caseTask;

  const verdict = gateVerdict(task);
  if (verdict === undefined) return null;

  const inFlight = pendingTaskName === task.name;
  // Mirrors TaskTable's `actionable`: a pending (engine mid-transition) or
  // terminal gate renders read-only.
  const actionable =
    task.state !== "pending" &&
    task.state !== "complete" &&
    task.state !== "rejected";

  return (
    <ReviewCard
      task={task}
      verdict={verdict}
      isException={false}
      actionable={actionable}
      onApprove={() => issue(task, "complete")}
      onReject={() => issue(task, "rejected")}
      // Never called: onResolve is the exception gate's control, and a case
      // review gate is never an exception gate (isException is false above).
      onResolve={() => {}}
      inFlight={inFlight}
      inFlightState={inFlight ? pendingState : undefined}
      resolvePending={false}
    />
  );
}
